/**
 * Campanhas (Planos de Cobranca)
 *
 * Lista os Planos de Cobranca do tenant. Cada plano tem um codigo unico que
 * o gestor distribui aos clientes. O cliente resgata o codigo no app e recebe
 * a cobranca conforme as regras configuradas.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, useToast } from "@payflow/ui";
import {
  chargePlansService,
  type ChargePlan,
  type ChargePlanStatus,
} from "../../services/charge-plans.service";

const STATUS_KEY: Record<ChargePlanStatus, { i18n: string; tone: string }> = {
  ativo: { i18n: "campaigns.status.ativo", tone: "bg-emerald-100 text-emerald-700" },
  inativo: { i18n: "campaigns.status.inativo", tone: "bg-slate-100 text-slate-700" },
  rascunho: { i18n: "campaigns.status.rascunho", tone: "bg-amber-100 text-amber-700" },
};

const FILTERS: { value: "all" | ChargePlanStatus; key: string }[] = [
  { value: "all", key: "campaigns.filters.all" },
  { value: "ativo", key: "campaigns.filters.ativo" },
  { value: "rascunho", key: "campaigns.filters.rascunho" },
  { value: "inativo", key: "campaigns.filters.inativo" },
];

export function CampaignsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [plans, setPlans] = useState<ChargePlan[]>([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | ChargePlanStatus>("ativo");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await chargePlansService.list({
        status: filter === "all" ? undefined : filter,
        search: search || undefined,
        limit: 50,
      });
      setPlans(items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCopyCodigo = async (codigo: string) => {
    try {
      await navigator.clipboard.writeText(codigo);
      toast({
        title: t("campaigns.toast.code_copied"),
        description: codigo,
        variant: "success",
      });
    } catch {
      toast({ title: t("campaigns.toast.code_copy_error"), variant: "error" });
    }
  };

  const handleDelete = async (plan: ChargePlan) => {
    if (!confirm(t("campaigns.confirm_delete", { name: plan.nome }))) return;
    try {
      await chargePlansService.remove(plan.id);
      toast({ title: t("campaigns.toast.deleted"), variant: "success" });
      load();
    } catch (e) {
      toast({
        title: t("campaigns.toast.delete_error"),
        description: (e as Error).message,
        variant: "error",
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("campaigns.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("campaigns.subtitle")}
          </p>
        </div>
        <Link to="/dashboard/campanhas/nova">
          <Button>{t("campaigns.new_button")}</Button>
        </Link>
      </header>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                filter === f.value
                  ? "bg-primary-600 text-white"
                  : "border border-border bg-card text-foreground hover:bg-muted"
              }`}
            >
              {t(f.key)}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder={t("campaigns.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 w-full rounded-lg border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:max-w-md md:flex-1"
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/5 p-4 text-sm text-error">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-2xl border border-border bg-card p-12 text-center text-sm text-muted-foreground">
          {t("common.loading")}
        </div>
      ) : plans.length === 0 ? (
        <EmptyState onCreate={() => navigate("/dashboard/campanhas/nova")} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              onOpen={() => navigate(`/dashboard/campanhas/${p.id}`)}
              onCopy={() => handleCopyCodigo(p.codigo)}
              onDelete={() => handleDelete(p)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  onOpen,
  onCopy,
  onDelete,
}: {
  plan: ChargePlan;
  onOpen: () => void;
  onCopy: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const status = STATUS_KEY[plan.status];
  return (
    <article className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3
            className="cursor-pointer text-base font-semibold text-foreground hover:text-primary-700"
            onClick={onOpen}
          >
            {plan.nome}
          </h3>
          <span
            className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${status.tone}`}
          >
            {t(status.i18n)}
          </span>
        </div>
      </header>

      <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
        <code className="flex-1 truncate text-sm font-mono text-foreground">
          {plan.codigo}
        </code>
        <button
          onClick={onCopy}
          className="rounded p-1 text-muted-foreground hover:bg-card hover:text-foreground"
          title={t("campaigns.copy_code_tooltip")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
        </button>
      </div>

      <dl className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="text-muted-foreground">{t("campaigns.card.modality")}</dt>
          <dd className="mt-0.5 font-medium text-foreground">
            {plan.tipoValor === "fixo"
              ? t("campaigns.card.modality_fixed")
              : t("campaigns.card.modality_client")}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            {t("campaigns.card.range")}
          </dt>
          <dd className="mt-0.5 font-medium text-foreground">
            {/*
              Apos a inversao da logica, ambos os modos (fixo e escolhido_cliente)
              usam faixa min/max. No modo "Eu defino" (fixo), valorStep representa
              o incremento por renovacao (so mostra se > 0).
            */}
            {`${formatBRL(plan.valorMinimo)} - ${formatBRL(plan.valorMaximo)}`}
            {plan.tipoValor === "fixo" &&
            plan.valorStep &&
            parseFloat(plan.valorStep) > 0 ? (
              <span className="ml-1 text-xs text-muted-foreground">
                (+{formatBRL(plan.valorStep)})
              </span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t("campaigns.card.subplans")}</dt>
          <dd className="mt-0.5 font-medium text-foreground">
            {plan.installmentsCount ?? plan.installments?.length ?? 0}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t("campaigns.card.renewal")}</dt>
          <dd className="mt-0.5 font-medium text-foreground">
            {plan.renovacaoHabilitada
              ? t("campaigns.card.renewal_active")
              : t("campaigns.card.renewal_disabled")}
          </dd>
        </div>
      </dl>

      <footer className="flex justify-end gap-2 border-t border-border pt-3">
        <button
          onClick={onDelete}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-error hover:bg-error/10"
        >
          {t("common.delete")}
        </button>
        <button
          onClick={onOpen}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
        >
          {t("common.open")}
        </button>
      </footer>
    </article>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-2xl">
        🎯
      </div>
      <h3 className="text-base font-semibold text-foreground">
        {t("campaigns.empty.title")}
      </h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        {t("campaigns.empty.description")}
      </p>
      <Button onClick={onCreate} className="mt-5">
        {t("campaigns.empty.cta")}
      </Button>
    </div>
  );
}

function formatBRL(v?: string | null): string {
  if (!v) return "—";
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
